import { useState, useMemo } from 'react';
import { formatCurrency } from '@peles/shared';
import { Plus, DollarSign } from 'lucide-react';
import type {
  CommissionWithRelations,
  CreateCommissionInput,
  UpdateCommissionInput,
} from '@peles/shared';
import {
  useCommissions,
  useCreateCommission,
  useUpdateCommission,
  useDeleteCommission,
} from '../../hooks/useCommissions';
import { EntityList, EntityModal } from '../common';
import type { EntityListColumn } from '../common/EntityList';
import { CommissionModal } from './CommissionModal';

interface Props {
  vendorId?: string;
  eventId?: string;
}

function formatDate(d: Date | string) {
  const dt = new Date(d);
  return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()).toLocaleDateString(
    'en-US',
    { month: 'short', day: 'numeric', year: 'numeric' },
  );
}

export function CommissionListView({ vendorId, eventId }: Props) {
  const { data: commissions = [], isLoading } = useCommissions({ vendorId, eventId });
  const createMutation = useCreateCommission();
  const updateMutation = useUpdateCommission();
  const deleteMutation = useDeleteCommission();

  const [editingItem, setEditingItem] = useState<CommissionWithRelations | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const totals = useMemo(() => {
    const totalAmount = commissions.reduce((s, c) => s + c.amount, 0);
    const totalPaid = commissions.reduce((s, c) => s + c.paidAmount, 0);
    return { totalAmount, totalPaid, balance: totalAmount - totalPaid };
  }, [commissions]);

  const columns: EntityListColumn<CommissionWithRelations>[] = [
    {
      key: 'date',
      header: 'Date',
      render: (c) => formatDate(c.date),
      sortable: true,
      sortValue: (c) => new Date(c.date),
      width: 'w-28',
    },
    ...(!vendorId
      ? [
          {
            key: 'vendorName',
            header: 'Vendor',
            render: (c: CommissionWithRelations) => (
              <span className="font-medium text-gray-800">{c.vendorName}</span>
            ),
            sortable: true,
            sortValue: (c: CommissionWithRelations) => c.vendorName,
          },
        ]
      : []),
    ...(!eventId
      ? [
          {
            key: 'eventName',
            header: 'Event',
            render: (c: CommissionWithRelations) =>
              c.eventName ?? <span className="text-gray-400">—</span>,
            hideOnMobile: true,
          },
        ]
      : []),
    {
      key: 'amount',
      header: 'Amount',
      render: (c) => (
        <span className="font-medium">{formatCurrency(c.amount, { wholeDollars: true })}</span>
      ),
      align: 'right',
      sortable: true,
      sortValue: (c) => c.amount,
      width: 'w-28',
    },
    {
      key: 'paidAmount',
      header: 'Paid',
      render: (c) => (
        <span className={c.paidAmount >= c.amount ? 'text-green-600' : 'text-amber-600'}>
          {formatCurrency(c.paidAmount, { wholeDollars: true })}
        </span>
      ),
      align: 'right',
      width: 'w-28',
      hideOnMobile: true,
    },
    {
      key: 'paidDate',
      header: 'Paid Date',
      render: (c) =>
        c.paidDate ? formatDate(c.paidDate) : <span className="text-gray-400">—</span>,
      hideOnMobile: true,
      width: 'w-28',
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (c) =>
        c.notes ? (
          <span className="text-gray-500 text-sm truncate max-w-xs block">{c.notes}</span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
      hideOnMobile: true,
    },
  ];

  async function handleCreate(data: CreateCommissionInput | UpdateCommissionInput) {
    await createMutation.mutateAsync(data as CreateCommissionInput);
    setShowCreate(false);
  }

  async function handleUpdate(data: CreateCommissionInput | UpdateCommissionInput) {
    if (!editingItem) return;
    await updateMutation.mutateAsync({
      id: editingItem.id,
      updates: data as UpdateCommissionInput,
    });
    setEditingItem(null);
  }

  async function handleDelete(id: string) {
    await deleteMutation.mutateAsync(id);
    setDeleteConfirmId(null);
  }

  const deleteTarget = commissions.find((c) => c.id === deleteConfirmId);

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      {commissions.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total Commissions', value: totals.totalAmount },
            { label: 'Total Paid', value: totals.totalPaid },
            { label: 'Outstanding', value: totals.balance },
          ].map(({ label, value }) => (
            <div key={label} className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className="font-semibold text-gray-900 text-sm">
                {formatCurrency(value, { wholeDollars: true })}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* List */}
      <EntityList
        items={commissions}
        columns={columns}
        getKey={(c) => c.id}
        isLoading={isLoading}
        onEdit={(c) => setEditingItem(c)}
        defaultSortBy="date"
        defaultSortDir="desc"
        searchPlaceholder="Search commissions..."
        filterBar={
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
          >
            <Plus size={14} />
            Add Commission
          </button>
        }
        emptyMessage="No commissions yet"
        emptyIcon={<DollarSign size={32} className="text-gray-300" />}
      />

      {/* Create Modal */}
      {showCreate && (
        <CommissionModal
          open={showCreate}
          onClose={() => setShowCreate(false)}
          onSave={handleCreate}
          defaultVendorId={vendorId}
          defaultEventId={eventId}
          isLoading={createMutation.isPending}
        />
      )}

      {/* Edit Modal */}
      {editingItem && (
        <CommissionModal
          open={!!editingItem}
          onClose={() => setEditingItem(null)}
          onSave={handleUpdate}
          defaultVendorId={vendorId}
          defaultEventId={eventId}
          initialValues={editingItem}
          isLoading={updateMutation.isPending}
          onDelete={() => setDeleteConfirmId(editingItem.id)}
        />
      )}

      {/* Delete confirm */}
      {deleteConfirmId && deleteTarget && (
        <EntityModal
          open={!!deleteConfirmId}
          onClose={() => setDeleteConfirmId(null)}
          title="Delete Commission"
          size="sm"
          footer={
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 text-sm text-gray-600 border rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          }
        >
          <p className="text-sm text-gray-600">
            Delete commission of{' '}
            <strong>{formatCurrency(deleteTarget.amount, { wholeDollars: true })}</strong> from{' '}
            <strong>{deleteTarget.vendorName}</strong>? This cannot be undone.
          </p>
        </EntityModal>
      )}
    </div>
  );
}
